import React, { useEffect, useState, useCallback, useRef } from "react";
import AppModal from "../../AppModal";
import AppModalHeader from "../../AppModal/AppModalHeader";
import AppModalContent from "../../AppModal/AppModalContent";
import AppInput from "../../AppInput";
import AppButton from "../../AppButton";
import { fetcher } from "../../../services/api";
import RequestSuccessModal from "./RequestSuccessModal";
import TrashIcon from "../../../assets/icons/trash";
import AppSelect from "../../AppSelect";
import { useValidationErrors } from "../../../hooks/useValidationErrors";
import Loader from "../../Loader/Loader";
import "./RequestProductionModal.css";

interface RequestProductionModalProps {
  isVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  onClose: () => void;
  prefillData: {
    ad_store_url: string;
    appName: string;
    clientKey: string;
  } | null;
  clientKey: string;
  onRequestProduction: () => void;
}

type Option = {
  name: string;
  value: string;
};

const RequestProductionModal: React.FC<RequestProductionModalProps> = ({
  isVisible,
  setIsModalVisible,
  onClose,
  prefillData,
  clientKey,
  onRequestProduction,
}) => {
  const [formData, setFormData] = useState({
    appName: "",
    ad_store_url: "",
    application_type: [] as string[],
    monthly_active_users: "",
    production_review_file_id: null as File | null,
    file: "",
  });
  const { errors, setApiErrors, clearError } =
    useValidationErrors<typeof formData>();
  const [videoFileId, setVideoFileId] = useState<string | null>(null);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [appTypes, setAppTypes] = useState<Option[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isVisible && prefillData) {
      setFormData((prev) => ({
        ...prev,
        appName: prefillData.appName || prev.appName,
        ad_store_url: prefillData.ad_store_url || prev.ad_store_url,
      }));
    } else if (!isVisible) {
      setFormData({
        appName: "",
        ad_store_url: "",
        application_type: [],
        monthly_active_users: "",
        production_review_file_id: null,
        file: "",
      });
      setApiErrors({});
      setVideoFileId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, prefillData]);

  useEffect(() => {
    const getAppTypes = async () => {
      try {
        const res = await fetcher("/publisher/client/application-types");
        const data = res.data as { data?: Option[] };
        const types: Option[] = Array.isArray(data.data) ? data.data : [];
        setAppTypes(types);
      } catch (err) {
        console.error("Failed to fetch application types:", err);
      }
    };

    if (isVisible) {
      getAppTypes();
    }
  }, [isVisible]);

  const updateFormData = useCallback(
    (key: keyof typeof formData, value: any) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      clearError(key);
    },
    [clearError]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submissionData = new FormData();
    submissionData.append("appName", formData.appName);
    submissionData.append("url", formData.ad_store_url);
    submissionData.append(
      "application_type",
      formData.application_type.join(",")
    );
    submissionData.append(
      "monthly_active_users",
      formData.monthly_active_users
    );

    if (videoFileId) {
      submissionData.append("file_id", videoFileId);
    }

    try {
      const { data, error } = await fetcher<any>(
        `/publisher/client/${clientKey}/upgrade-to-production`,
        {
          method: "POST",
          body: submissionData,
        }
      );
      if (data) {
        onRequestProduction?.();
        setIsModalVisible(false);
        setIsSuccessModalVisible(true);
        onClose();
      } else if (error) {
        setApiErrors(error);
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const handleRemoveVideo = () => {
    setFormData((prev) => ({ ...prev, production_review_file_id: null }));
    setVideoFileId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <AppModal
        isVisible={isVisible}
        onChange={() => setIsModalVisible(!isVisible)}
      >
        <AppModalHeader onClose={() => setIsModalVisible(false)}>
          Upgrade to Production Key
        </AppModalHeader>
        <AppModalContent>
          <div className="modal-description">
            Submit the form to upgrade your Test API key to a Production key.
            The request may take few business day to process. Our team may
            contact you by email if additional details are needed.
          </div>
          <form className="modal-form" onSubmit={handleSubmit}>
            <span className="modal-form-subtitle">App Details</span>
            <div className="for-auto-fill">
              <div className="info-card">
                <div className="info-block">
                  <p>Company Name</p>
                  <span>{formData.appName}</span>
                </div>
                <div className="divider"></div>
                <div className="info-block">
                  <p>URL</p>
                  <span>{formData.ad_store_url}</span>
                </div>
              </div>
            </div>

            <span className="modal-form-subtitle">Other Details</span>
            <AppSelect
              name="application_type"
              placeholder="App Category - e.g. Messaging, Dating, Keyboard, Social Media"
              options={appTypes}
              value={formData.application_type}
              onSelect={(selected: (string | number)[]) => {
                updateFormData("application_type", selected as string[]);
              }}
              isDark
              noAdditionalName
            />

            <AppInput
              type="number"
              id="monthly_active_users"
              label="Number of Monthly Active Users"
              value={formData.monthly_active_users || ""}
              onChange={(e) =>
                updateFormData("monthly_active_users", Number(e.target.value))
              }
              error={errors.monthly_active_users}
              min={0}
            />

            <label className="upload-label">
              <span>
                Upload a file (video recording) showing KLIPY integrated into
                your app. Be sure to include all relevant screens and
                demonstrate sending a GIF, Clip, Meme, Sticker, or any other
                KLIPY feature you've integrated.
                <br />
                Max file size: 25 MB
              </span>
            </label>

           <div className="download-paragraph">
             <p>
              Please ensure integrations includes KLIPY Attribution. <a
                href="/powered-by-KLIPY.zip"
                download
                target="_blank"
                rel="noopener noreferrer"
              >
                Download them here.
              </a>
            </p>
            </div>   
           
            <div className="modal-actions">
              {formData.production_review_file_id ? (
                <div className="attached-file-display">
                  {isUploading ? (
                    <Loader />
                  ) : (
                    <>
                      <div className="attached-file-info">
                        <span className="attached-file-label">
                          Attached File
                        </span>
                        <span className="attached-file-name">
                          {formData.production_review_file_id.name.length > 7
                            ? formData.production_review_file_id.name.substring(
                                0,
                                7
                              ) + "..."
                            : formData.production_review_file_id.name}{" "}
                          (
                          {(
                            formData.production_review_file_id.size /
                            (1024 * 1024)
                          ).toFixed(1)}{" "}
                          MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        className="remove-file-button"
                        onClick={handleRemoveVideo}
                      >
                        <TrashIcon />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <AppButton
                  variant="third"
                  fullWidth
                  type="button"
                  onClick={() =>
                    (
                      document.querySelector(
                        'input[type="file"]'
                      ) as HTMLInputElement | null
                    )?.click()
                  }
                >
                  Attach File
                </AppButton>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4, video/mov, video/webm, application/pdf, image/png, image/jpg, image/jpeg"
                onChange={async (e) => {
                  const file = e.target.files?.[0] || null;
                  updateFormData("production_review_file_id", file);

                  if (file) {
                    setIsUploading(true);
                    setApiErrors({});
                    const videoFormData = new FormData();
                    videoFormData.append(
                      "type",
                      "publisher_upgrade_to_production"
                    );
                    videoFormData.append("file", file);

                    const { data, error } = await fetcher<any>("/file", {
                      method: "POST",
                      body: videoFormData,
                    });

                    if (data) {
                      const uuid = data?.data?.uuid;
                      if (uuid) {
                        setVideoFileId(uuid);
                        setApiErrors({ production_review_file_id: "" });
                        setIsUploading(false);
                      } else {
                        setIsUploading(false);
                      }
                    } else if (error) {
                      setApiErrors(error);
                      setIsUploading(false);
                      updateFormData("production_review_file_id", null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }
                  }
                }}
              />
              <AppButton
                variant="secondary"
                type="submit"
                fullWidth
                disabled={isUploading}
              >
                Apply
              </AppButton>
            </div>
            {errors.production_review_file_id && (
              <p className="input-error">
                A file (video recording) field is required.
              </p>
            )}
            {errors.file && <p className="input-error">{errors.file}</p>}
          </form>
        </AppModalContent>
      </AppModal>
      <RequestSuccessModal
        isVisible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
      />
    </>
  );
};

export default RequestProductionModal;
